import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { ISalesDeduction } from "../../../../types/Sales";
import { formatDate } from "../../../../utils/Date";
import { numberFormatter } from "../../../../utils/Formatter/Global";
import { useSalesDeductionColumnDefs } from "./columnDefs";
import CustomizedScrollbar from "../../../Custom/CustomizedScrollbar";
import CloseIcon from "@mui/icons-material/Close";
import { useConfirmation } from "../../../../hooks/use-confirmation";
import { useState } from "react";
import ConfirmationModal from "../../../UI/Modal/ConfirmationModal";

type Props = {
  data: ISalesDeduction[];
  documentType: string;
  onDeleteHandler?: (id?: number) => void;
};

const SalesDeductionTable = ({
  data,
  documentType,
  onDeleteHandler,
}: Props) => {
  const [deletingId, setDeleingId] = useState<number>();
  const headers = useSalesDeductionColumnDefs(documentType);
  const paddingTable = {
    px: 1.5,
    py: 1.5,
  };

  const {
    confirmation: cancelConfirmation,
    openConfirmationHandler: openCancelConfirmation,
    closeConfirmationHandler: closeCancelConfirmation,
    submitConfirmationHandler: submitCancelConfirmation,
  } = useConfirmation(() => {
    if (onDeleteHandler) onDeleteHandler(deletingId);
  });

  return (
    <TableContainer>
      <Table aria-label="simple table">
        <CustomizedScrollbar
          sx={{ width: "100%", py: 1 }}
          forceVisible="y"
          autoHide={false}
        >
          <TableHead sx={{ bgcolor: (theme) => theme.palette.primary.light }}>
            <TableRow>
              {headers.map((header) => (
                <TableCell
                  key={header.thaiLabel}
                  align={header.align}
                  sx={paddingTable}
                  width={header.width}
                >
                  <Typography fontWeight={600} width={header.width}>
                    {header.thaiLabel}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((deduction) => (
              <TableRow
                key={`${deduction.deduction_issue_date}-${deduction.document_type}-${deduction.document_unique_id}`}
              >
                <TableCell align={headers[0]?.align}>
                  <Typography width={headers[0]?.width}>
                    {formatDate(deduction.deduction_issue_date)}
                  </Typography>
                </TableCell>
                <TableCell align={headers[1]?.align}>
                  <Typography width={headers[1]?.width}>
                    {deduction.document_type === "external_document"
                      ? "เอกสารภายนอก"
                      : "ใบมัดจำ"}
                  </Typography>
                </TableCell>
                <TableCell align={headers[2]?.align}>
                  <Typography width={headers[2]?.width}>
                    {deduction.document_unique_id || "-"}
                  </Typography>
                </TableCell>
                <TableCell align={headers[3]?.align}>
                  <Typography width={headers[3]?.width}>
                    {numberFormatter(deduction.deduction_amount)}
                  </Typography>
                </TableCell>
                <TableCell align={headers[4]?.align}>
                  <Typography width={headers[4]?.width}>
                    {numberFormatter(
                      deduction.credit_balance ? deduction.credit_balance : 0
                    )}
                  </Typography>
                </TableCell>
                <TableCell align={headers[5]?.align}>
                  <Typography width={headers[5]?.width}>
                    {deduction.remark || "-"}
                  </Typography>
                </TableCell>
                {documentType === "sales_invoice" && (
                  <TableCell align={headers[6]?.align}>
                    <IconButton
                      onClick={() => {
                        openCancelConfirmation();
                        setDeleingId(deduction.id);
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {data.length === 0 && (
              <TableRow sx={{ position: "relative", height: 50 }}>
                <Typography
                  sx={{
                    position: "sticky",
                    top: "70%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                  }}
                >
                  ไม่มีข้อมูล
                </Typography>
              </TableRow>
            )}
          </TableBody>
        </CustomizedScrollbar>
      </Table>
      <ConfirmationModal
        title="ยกเลิกการตัดชำระ"
        message="หากทำการยกเลิกจะไม่สามารถย้อนกลับได้"
        open={cancelConfirmation}
        handleClose={closeCancelConfirmation}
        action={submitCancelConfirmation}
      />
    </TableContainer>
  );
};

export default SalesDeductionTable;
